// @flow
import { unescapeHtml } from '@ta/common.text-html';
import type { TypeaheadItem } from '../types';

export const getSearchUrl = (query: string, searchSessionId: string) =>
  `/Search?${new URLSearchParams({
    q: query,
    searchSessionId,
  }).toString()}`;

/**
 * @param res - the untyped response from the typeahead API
 * @param searchSessionId
 * @return a list of search results in a well defined strongly-typed format
 * @todo this should be unnecessary when this data is fetched from GraphQL
 */
const parseSearchResults = (res: any, searchSessionId: string): TypeaheadItem[] => {
  const isUnscoped = (+res.query.parentids || 0) <= 1 && !res.query.isNearby;

  const items = [];
  res.results.forEach(result => {
    let { url } = result;

    // Special case - we don't navigate to keyword URLs unless we're scoped. These URLs don't work
    // without a geo.
    if (isUnscoped && (result.type === 'KEYWORD' || result.data_type === 'TAG')) {
      url = getSearchUrl(result.name, searchSessionId);
    } else if (result.data_type === 'SEARCH') {
      url = getSearchUrl(result.query, searchSessionId);
    }

    if (result.data_type == null) {
      items.push({
        title: result.details.name,
        subtitle: result.details.geo_name || null,
        image: null,
        url,
        details: {
          type: 'SEARCH_SUGGESTION',
        },
        key: `SEARCH_${items.length + 1}`,
      });
      return;
    }

    switch (result.data_type) {
      case 'LOCATION':
        items.push({
          title: result.details.name,
          subtitle: result.details.geo_name || null,
          image: null,
          url,
          details: {
            type: 'LOCATION',
            locationId: result.value,
          },
          key: `LOCATION_${result.value}`,
        });
        break;

      case 'USER_PROFILE':
        items.push({
          title: unescapeHtml(result.details.name), // Display names are HTML escaped
          subtitle: `@${result.details.user_name} \u2022 ${result.details.followers}`,
          image: null,
          url,
          details: {
            type: 'USER',
            username: result.details.user_name,
            verified: result.details.verified,
          },
          key: `USER_${result.details.user_name}`,
        });
        break;

      case 'QUERY_SUGGESTION':
        items.push({
          title: result.name,
          subtitle: null,
          image: null,
          url,
          details: {
            type: 'SEARCH_SUGGESTION',
          },
          key: result.document_id,
        });
        break;

      case 'PAGE': {
        items.push({
          title: result.name,
          subtitle: result.details.geo_name || null,
          image: null,
          url,
          details: {
            type: 'SEARCH_SUGGESTION',
          },
          key: `SEARCH_${items.length + 1}`,
        });
        break;
      }

      case 'TAG':
        items.push({
          title: result.details.name,
          subtitle: result.details.geo_name || null,
          image: null,
          url,
          details: {
            type: 'SEARCH_SUGGESTION',
          },
          key: `TAG_${items.length + 1}`,
        });
        break;

      case 'NEAR_POI':
        items.push({
          title: result.find_near_input,
          subtitle: result.details.geo_name || null,
          image: null,
          url,
          details: {
            type: 'SEARCH_SUGGESTION',
          },
          key: `NEAR_POI_${items.length + 1}`,
        });
        break;

      case 'SEARCH':
        items.push({
          title: result.query,
          subtitle: null,
          image: null,
          url,
          details: {
            type: 'SEARCH',
          },
          key: `SEARCH_${items.length + 1}`,
        });
        break;

      default:
        break;
    }
  });

  return items;
};

export default parseSearchResults;
